import {Show} from 'platform/foundation';
import {match, Pattern} from 'ts-pattern';

import {memo, MouseEvent} from 'react';

import {isNotNil} from 'ramda';

import {
  CELL_FOCUS_STYLE,
  COLUMN_SEPARATOR_STYLE,
  PINNED_LEFT_BOUNDARY_LINE_STYLE,
  PINNED_LEFT_EDGE_LINE_STYLE,
  PINNED_RIGHT_EDGE_LINE_STYLE,
  RESIZE_INDICATOR_LEFT_STYLE,
  RESIZE_INDICATOR_STYLE,
  ROW_BORDER_BOTTOM,
} from '../../../../../../constants/borderStyles';
import {DEFAULT_COL_WIDTH} from '../../../../../../constants/defaultColWidth';
import type {TruncatedCellHoverMode} from '../../../../../../types/Api';
import type {EagColumn} from '../../../../../../types/EagColumn';
import type {EagColumnState} from '../../../../../../types/EagColumnState';
import type {RowData} from '../../../../../../types/RowData';
import {getStickyCellBackgroundClass} from '../../../../../../utils/getStickyCellBackgroundClass';
import {getValueFromRawCell} from '../../../../../../utils/getValueFromRawCell';
import {hasTextSelectionWithin} from '../../../../../../utils/hasTextSelectionWithin';
import {isWrappableType} from '../../../../../../utils/isWrappableType';
import {
  getMobileRowPaddingClass,
  getRowPaddingClass,
  getRowVerticalPaddingPx,
  LineHeightToPx,
  type LineHeight,
} from '../../../../../../utils/lineHeightToValue';
import {renderCell} from '../../../../../../utils/renderCell';
import {getSkeletonBarClass} from '../../../../getSkeletonBarClass';
import {ExpandedCellOverlay} from './components/ExpandedCellOverlay/ExpandedCellOverlay';
import {TruncatedCellTooltip} from './components/TruncatedCellTooltip/TruncatedCellTooltip';
import {useTruncatedCellHover} from './hooks/useTruncatedCellHover';

// Resize state is subscribed once per row in GridRow and passed down as
// `isBeingResized`. Drag visuals (source opacity, drop-target background, drop-side
// line) are applied via a `<style>` element injected by `useGridDragAndDrop`,
// scoped to `td[data-column="..."]`. This keeps every-cell re-renders out of the
// column-drag hot path.
// verticalSeparators, valueWrapMode, truncatedCellHoverMode, emptyCellValue,
// rowHeight, and isColumnPending are passed as props from GridRow to avoid
// per-cell store subscriptions for rarely-changing state.

export type GridCellProps = {
  column: EagColumn;
  columnState: EagColumnState;
  row: RowData;
  pinnedLeftOffset?: number;
  pinnedRightOffset?: number;
  verticalSeparators: boolean;
  valueWrapMode: string;
  /** What reveals the full content of a truncated cell on hover: tooltip, in-place expand, or nothing. */
  truncatedCellHoverMode: TruncatedCellHoverMode;
  /** Placeholder renderers display for cells with no value — '' (nothing) or '–' (en dash),
   * resolved from the emptyCellValueMode setting once per row in GridRow. */
  emptyCellValue?: string;
  rowHeight: LineHeight;
  pinnedEdgeShadowClass?: string;
  /** Suppress the right-edge column separator on the last data column when an
   * actions column immediately follows — the actions column already renders its
   * own left-edge border, so the two would otherwise form a double line. */
  isLastBeforeActions?: boolean;
  /** True when this specific cell is keyboard-focused. */
  isCellFocused?: boolean;
  /** True while this column is being resized — drives the resize indicator.
   * Subscribed once per row in GridRow (not per cell). */
  isBeingResized: boolean;
  /** True while this column's delta fetch is in flight — cells whose row lacks
   * the field render a loading skeleton. Subscribed once per row in GridRow. */
  isColumnPending?: boolean;
  horizontalSeparators: boolean;
  /** True on mobile viewports — selects the wider touch-friendly padding class. */
  isMobile?: boolean;
  /** True when the row is selected — pinned cells must mirror the row's blue
   * selection background instead of staying white (T20-134467). */
  isRowSelected?: boolean;
  /** The backend highlighted the row; pinned cells mirror it like the selection. */
  isRowHighlighted?: boolean;
  /** True on the LAST left-pinned column — its right edge renders the elevation
   * boundary shade instead of the plain separator line. */
  isLastPinnedLeft?: boolean;
};

// memo: rendered N×M times (rows × columns); prevents cascading re-renders from parent.
export const GridCell = memo((props: GridCellProps) => {
  const rawValue = props.row[props.column.field];
  const value = getValueFromRawCell(props.column, rawValue);
  const width = props.columnState.width ?? DEFAULT_COL_WIDTH;

  // Cell-level loading: the column was just shown and its delta fetch is in
  // flight. Only skeleton cells whose row is actually missing the field —
  // rows that already carry data (e.g. a fresh page fetch that included the
  // column) render their real value.
  const showSkeleton = (props.isColumnPending ?? false) && rawValue === undefined;

  const pinnedStyle = match(props.columnState.pinned)
    .with('LEFT', () => ({
      position: 'sticky' as const,
      left: props.pinnedLeftOffset ?? 0,
      zIndex: 'var(--z-sidebar)',
    }))
    .with('RIGHT', () => ({
      position: 'sticky' as const,
      right: props.pinnedRightOffset ?? 0,
      zIndex: 'var(--z-sidebar)',
    }))
    .otherwise(() => undefined);

  const isPinned = props.columnState.pinned === 'LEFT' || props.columnState.pinned === 'RIGHT';

  // Pinned cells need an opaque background (rows scroll beneath them), so they
  // mirror the row state explicitly; unpinned cells inherit the row background.
  const bgClass = isPinned
    ? getStickyCellBackgroundClass(props.isRowSelected ?? false, props.isRowHighlighted ?? false)
    : 'bg-inherit';

  const isWrap = props.valueWrapMode === 'WRAP' && isWrappableType(props.column.type);

  // In WRAP mode descendants get `!whitespace-normal !break-words` to override
  // the inner renderer divs (which use `truncate`, applying `whitespace-nowrap`).
  // FLAG and TAG cells wrap by flowing whole chips onto the next line (HStack
  // flex-wrap), so they skip the descendant override — it would wrap the text
  // inside each chip, defeating the Flag label's own nowrap/ellipsis.
  const wrapClass = match({isWrap, type: props.column.type})
    .with({isWrap: true, type: Pattern.union('FLAG', 'TAG')}, () => 'whitespace-normal')
    .with(
      {isWrap: true},
      () => 'whitespace-normal break-words [&_*]:!whitespace-normal [&_*]:!break-words'
    )
    .otherwise(() => 'text-ellipsis whitespace-nowrap');

  const paddingClass = props.isMobile
    ? getMobileRowPaddingClass[props.rowHeight]
    : getRowPaddingClass[props.rowHeight];
  const rowHeightPx = LineHeightToPx[props.rowHeight];
  // Image height = row height minus the vertical padding applied to the <td>.
  // This ensures the image + its surrounding padding fits exactly within the row.
  const thumbnailHeightPx = rowHeightPx - getRowVerticalPaddingPx[props.rowHeight];

  const shadowClass = props.pinnedEdgeShadowClass ?? '';

  const truncatedHover = useTruncatedCellHover(!isWrap && props.truncatedCellHoverMode !== 'NONE');
  const isTooltipHoverActive =
    isNotNil(truncatedHover.hoverState) && props.truncatedCellHoverMode === 'TOOLTIP';

  const cellContent = showSkeleton ? (
    <div data-testid="eagGridCellSkeleton" className={getSkeletonBarClass(props.column.type)} />
  ) : (
    renderCell(props.column, value, props.row, thumbnailHeightPx, isWrap, props.emptyCellValue)
  );

  // What the hover reveal (tooltip or expanded overlay) shows: FLAG and TAG cells
  // reuse their actual content so the colored badges survive the reveal;
  // everything else shows the measured plain text.
  const truncatedHoverContent = isNotNil(truncatedHover.hoverState)
    ? match(props.column.type)
        .with(Pattern.union('FLAG', 'TAG'), () => cellContent)
        .otherwise(() => truncatedHover.hoverState?.label)
    : null;

  // Suppress the row's onClick (which navigates) when the user is selecting
  // text inside the cell, so they can copy without leaving the page.
  const handleClick = (event: MouseEvent<HTMLTableCellElement>) => {
    if (hasTextSelectionWithin(event.currentTarget)) {
      event.stopPropagation();
    }
  };

  return (
    <td
      className={`relative overflow-hidden text-[13px] text-[#091E42] ${paddingClass} ${wrapClass} ${bgClass} ${shadowClass}`}
      style={{
        width,
        maxWidth: width,
        height: rowHeightPx,
        ...(isWrap ? {} : {maxHeight: rowHeightPx}),
        ...pinnedStyle,
        ...(props.horizontalSeparators ? ROW_BORDER_BOTTOM : {}),
        ...(props.isCellFocused ? CELL_FOCUS_STYLE : {}),
      }}
      data-column={props.column.key}
      tabIndex={props.isCellFocused ? 0 : -1}
      onClick={handleClick}
      onMouseEnter={truncatedHover.handleMouseEnter}
      onMouseLeave={truncatedHover.hide}
      onWheel={truncatedHover.hide}
      onFocus={truncatedHover.handleFocus}
      onBlur={truncatedHover.hide}
    >
      {cellContent}
      {/* Both reveals render as siblings (not wrappers) so the cell content DOM
          stays stable when they open. The tooltip is controlled (isOpen) — the
          hover delay and the "is the content actually clipped" measurement
          already happened in the hook. */}
      <Show when={isTooltipHoverActive}>
        <TruncatedCellTooltip label={truncatedHoverContent} />
      </Show>
      <Show when={props.truncatedCellHoverMode === 'EXPAND'}>
        <ExpandedCellOverlay
          hoverState={truncatedHover.hoverState}
          content={truncatedHoverContent}
          paddingClass={paddingClass}
        />
      </Show>
      {/* Right-pinned columns use left-side indicator to match the left-side resize handle */}
      <Show when={props.isBeingResized}>
        <span
          style={
            props.columnState.pinned === 'RIGHT'
              ? RESIZE_INDICATOR_LEFT_STYLE
              : RESIZE_INDICATOR_STYLE
          }
        />
      </Show>
      {/* Pinned edge line: always-visible separator for pinned columns (column separator is hidden for pinned).
          Elevation shade on the last left-pinned column — the pinned/scrollable boundary. */}
      <Show when={!props.isBeingResized && props.columnState.pinned === 'LEFT'}>
        <span
          style={
            props.isLastPinnedLeft ? PINNED_LEFT_BOUNDARY_LINE_STYLE : PINNED_LEFT_EDGE_LINE_STYLE
          }
        />
      </Show>
      <Show when={!props.isBeingResized && props.columnState.pinned === 'RIGHT'}>
        <span style={PINNED_RIGHT_EDGE_LINE_STYLE} />
      </Show>
      {/* Column separator: only on non-pinned cells, and not on the last cell
          before an actions column (which renders its own left-edge border). */}
      <Show
        when={
          !props.isBeingResized &&
          props.verticalSeparators &&
          !isPinned &&
          !props.isLastBeforeActions
        }
      >
        <span style={COLUMN_SEPARATOR_STYLE} />
      </Show>
    </td>
  );
});
